// @ts-check
import { Paperclip } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { formatDay, label, termsSummary, WHOLESALE_AGREEMENT_STATUS } from '../../utils/wholesale';
import FileUpload from '../FileUpload';
import Modal from '../ui/Modal';
import { card, EnumSelect, Field, input, primaryBtn, secondaryBtn } from './shared';

/** @param {{partnerId: string, agreement?: any, acceptedQuotes: any[], onClose: () => void, onSaved: () => void}} props */
const AgreementModal = ({ partnerId, agreement, acceptedQuotes, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState({
    quote_id: agreement?.quote_id || acceptedQuotes[0]?.id || '',
    signed_at: agreement?.signed_at || '',
    starts_on: agreement?.starts_on || '',
    ends_on: agreement?.ends_on || '',
    document_key: agreement?.document_key || '',
    status: agreement?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    const body = { ...form, signed_at: form.signed_at || null, starts_on: form.starts_on || null, ends_on: form.ends_on || null, document_key: form.document_key || null };
    setSaving(true);
    try {
      if (agreement) {
        const { quote_id: _quote, ...patch } = body;
        await adminWholesaleService.updateAgreement(agreement.id, patch);
      } else {
        await adminWholesaleService.addAgreement(partnerId, body);
      }
      toast.success('Agreement saved');
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to save the agreement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="lg" onClose={onClose}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">{agreement ? 'Edit agreement' : 'New agreement'}</h2>
      </div>
      <div className="px-6 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
        {!agreement && (
          <Field label="From the accepted quote">
            <select className={input} value={form.quote_id} onChange={(e) => set({ quote_id: e.target.value })}>
              {acceptedQuotes.map((q) => (
                <option key={q.id} value={q.id}>
                  {label(q.model)}: {termsSummary(q)}
                </option>
              ))}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Signed on">
            <input type="date" className={input} value={form.signed_at} onChange={(e) => set({ signed_at: e.target.value })} />
          </Field>
          <Field label="Starts on">
            <input type="date" className={input} value={form.starts_on} onChange={(e) => set({ starts_on: e.target.value })} />
          </Field>
          <Field label="Ends on">
            <input type="date" className={input} value={form.ends_on} min={form.starts_on} onChange={(e) => set({ ends_on: e.target.value })} />
          </Field>
        </div>
        <Field label="Countersigned sheet" hint="Required before the agreement can be made active">
          {form.document_key && (
            <a href={form.document_key} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm underline mb-2">
              <Paperclip size={14} /> Current sheet
            </a>
          )}
          <FileUpload uploadType="vendor-document" accept=".pdf,.doc,.docx" multiple={false} maxFiles={1} onUploadComplete={(files) => set({ document_key: files[0]?.url || '' })} />
        </Field>
        <Field label="Status">
          <EnumSelect value={form.status} onChange={(status) => set({ status })} options={Object.values(WHOLESALE_AGREEMENT_STATUS)} />
        </Field>
      </div>
      <div className="flex gap-2 px-6 py-4 border-t">
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} disabled={saving} className={secondaryBtn}>
          Cancel
        </button>
      </div>
    </Modal>
  );
};

/** @param {{partner: any, onChanged: () => void}} props */
const AgreementTab = ({ partner, onChanged }) => {
  const toast = useToast();
  const [quotes, setQuotes] = useState([]);
  const [modal, setModal] = useState(undefined);
  const agreements = partner.agreements;

  const load = useCallback(async () => {
    try {
      setQuotes(await adminWholesaleService.listQuotes(partner.id));
    } catch (err) {
      toast.error(err.message || 'Failed to load the quotes');
    }
  }, [partner.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const accepted = quotes.filter((q) => q.status === 'accepted');

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold">Agreement</h2>
        <button onClick={() => setModal(null)} disabled={accepted.length === 0} className={primaryBtn} title={accepted.length === 0 ? 'Accept a quote first' : ''}>
          New agreement
        </button>
      </div>
      {accepted.length === 0 && agreements.length === 0 && (
        <div className={`${card} p-8 text-center text-sm text-muted-foreground`}>An agreement is made from an accepted quote. Accept one on the Quotes tab first.</div>
      )}
      <div className="space-y-4">
        {agreements.map((a) => (
          <div key={a.id} className={`${card} p-5`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">{label(a.status)}</span>
              <button onClick={() => setModal(a)} className="text-sm underline">
                Edit
              </button>
            </div>
            <p className="text-sm">{termsSummary(a.terms_snapshot)}</p>
            <p className="text-sm text-muted-foreground mt-1">
              Signed {formatDay(a.signed_at)} · {formatDay(a.starts_on)} to {formatDay(a.ends_on)}
            </p>
            {a.document_key ? (
              <a href={a.document_key} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm underline mt-2">
                <Paperclip size={14} /> Countersigned sheet
              </a>
            ) : (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">No countersigned sheet attached yet.</p>
            )}
            <details className="mt-3">
              <summary className="text-xs text-muted-foreground cursor-pointer">Terms as signed</summary>
              <pre className="text-xs bg-muted/50 rounded-md p-3 mt-2 overflow-x-auto">{JSON.stringify(a.terms_snapshot, null, 2)}</pre>
            </details>
          </div>
        ))}
      </div>
      {modal !== undefined && (
        <AgreementModal
          partnerId={partner.id}
          agreement={modal || undefined}
          acceptedQuotes={accepted}
          onClose={() => setModal(undefined)}
          onSaved={() => {
            setModal(undefined);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default AgreementTab;
